import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase, getCurrentSession } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useMastermind } from "@/contexts/MastermindContext";
import { BUILTIN_SIDEBAR_SECTIONS } from "@shared/pluginManifest";
import { getRegistration, dropRegistration } from "./hostGlobals";
import { unregisterAllPluginTools } from "./pluginToolRegistry";
import { createPluginSdk, type PluginHostState, type PluginSdk } from "./sdk";
import type {
  InstalledPlugin,
  LoadedPlugin,
  PluginRootSection,
  PluginSidebarItem,
} from "./types";

const PLUGINS_BUCKET = "plugins";
const SCRIPT_ATTR = "data-deeplysmart-plugin";

type BuiltinSection = (typeof BUILTIN_SIDEBAR_SECTIONS)[number];

export interface PluginRouteEntry {
  pluginId: string;
  path: string;
  title: string;
}

interface PluginContextValue {
  /** All installed plugins (any status). */
  installed: InstalledPlugin[];
  isLoading: boolean;
  /** Successfully loaded ACTIVE plugins. */
  loaded: LoadedPlugin[];
  /** Load errors per plugin id (bundle failed, registration missing, …). */
  loadErrors: Record<string, string>;
  /** Routable pages of loaded plugins. */
  routes: PluginRouteEntry[];
  /** Sidebar entries grouped by builtin section. */
  sidebarBySection: Record<BuiltinSection, PluginSidebarItem[]>;
  /** Plugin-defined root sections. */
  rootSections: PluginRootSection[];
  getLoadedPlugin(pluginId: string): LoadedPlugin | undefined;
  activate(pluginId: string): Promise<void>;
  deactivate(pluginId: string): Promise<void>;
  uninstall(pluginId: string): Promise<void>;
  /** POST/GET to the plugin manager API with the Supabase Bearer token. */
  managerFetch(method: string, path: string, body?: unknown): Promise<Response>;
  refetchInstalled(): Promise<unknown>;
}

const PluginContext = createContext<PluginContextValue | null>(null);

function publicUrl(storagePath: string, file: string, version: string): string {
  const { data } = supabase.storage.from(PLUGINS_BUCKET).getPublicUrl(`${storagePath}/${file}`);
  // version query param busts the PWA NetworkFirst supabase cache on updates
  return `${data.publicUrl}?v=${encodeURIComponent(version)}`;
}

function loadScript(src: string, pluginId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.setAttribute(SCRIPT_ATTR, pluginId);
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load plugin bundle"));
    document.head.appendChild(script);
  });
}

function unloadPluginDom(pluginId: string): void {
  document
    .querySelectorAll(`script[${SCRIPT_ATTR}="${pluginId}"], link[${SCRIPT_ATTR}="${pluginId}"]`)
    .forEach((el) => el.remove());
}

function loadStyles(href: string, pluginId: string): void {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute(SCRIPT_ATTR, pluginId);
  document.head.appendChild(link);
}

export function PluginProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const { currentSpaceId, currentTopicId } = useMastermind();

  // Mutable host state the SDK reads at call time (no re-created SDK objects).
  const hostStateRef = useRef<PluginHostState>({
    userId: null,
    workspaceId: null,
    spaceId: null,
    topicId: null,
  });
  hostStateRef.current.userId = user?.id ?? null;
  hostStateRef.current.workspaceId = currentWorkspaceId ?? null;
  hostStateRef.current.spaceId = currentSpaceId ?? null;
  hostStateRef.current.topicId = currentTopicId ?? null;

  const {
    data: installed = [],
    isLoading,
    refetch: refetchInstalled,
  } = useQuery<InstalledPlugin[]>({
    queryKey: ["plugins", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plugins")
        .select("*")
        .order("installed_at", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as InstalledPlugin[];
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  const [loaded, setLoaded] = useState<LoadedPlugin[]>([]);
  const [loadErrors, setLoadErrors] = useState<Record<string, string>>({});
  const loadedIdsRef = useRef<Set<string>>(new Set());
  const loadedVersionRef = useRef<Map<string, string>>(new Map());
  const sdksRef = useRef<Map<string, PluginSdk>>(new Map());

  const loadPlugin = useCallback(async (plugin: InstalledPlugin): Promise<void> => {
    const id = plugin.plugin_id;
    if (loadedIdsRef.current.has(id)) return;
    loadedIdsRef.current.add(id);
    try {
      if (!getRegistration(id)) {
        await loadScript(publicUrl(plugin.storage_path, plugin.manifest.entry.client, plugin.version), id);
      }
      const registration = getRegistration(id);
      if (!registration) {
        throw new Error("Bundle loaded but registerPlugin() was never called");
      }
      if (plugin.manifest.entry.styles) {
        loadStyles(publicUrl(plugin.storage_path, plugin.manifest.entry.styles, plugin.version), id);
      }
      let sdk = sdksRef.current.get(id);
      if (!sdk) {
        sdk = createPluginSdk(plugin, hostStateRef.current);
        sdksRef.current.set(id, sdk);
      }
      loadedVersionRef.current.set(id, plugin.version);
      setLoaded((prev) => [...prev.filter((p) => p.plugin.plugin_id !== id), { plugin, registration, sdk }]);
      setLoadErrors((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      try {
        await registration.onActivate?.(sdk);
      } catch (e) {
        console.error(`[plugins] onActivate failed for "${id}"`, e);
      }
    } catch (e) {
      loadedIdsRef.current.delete(id);
      const message = e instanceof Error ? e.message : "Failed to load plugin";
      console.error(`[plugins] failed to load "${id}":`, message);
      setLoadErrors((prev) => ({ ...prev, [id]: message }));
    }
  }, []);

  const unloadPlugin = useCallback((pluginId: string): void => {
    const current = loadedIdsRef.current;
    if (!current.has(pluginId)) return;
    const entry = sdksRef.current.get(pluginId);
    const reg = getRegistration(pluginId);
    try {
      unregisterAllPluginTools(pluginId);
      reg?.onDeactivate?.(entry);
    } catch (e) {
      console.error(`[plugins] onDeactivate failed for "${pluginId}"`, e);
    }
    dropRegistration(pluginId);
    unloadPluginDom(pluginId);
    current.delete(pluginId);
    loadedVersionRef.current.delete(pluginId);
    setLoaded((prev) => prev.filter((p) => p.plugin.plugin_id !== pluginId));
  }, []);

  // Load active plugins / unload ones that are no longer active.
  useEffect(() => {
    if (!user) return;
    const activeIds = new Set(
      installed.filter((p) => p.status === "active").map((p) => p.plugin_id)
    );
    for (const plugin of installed) {
      if (plugin.status !== "active") continue;
      const loadedVersion = loadedVersionRef.current.get(plugin.plugin_id);
      if (loadedVersion && loadedVersion !== plugin.version) unloadPlugin(plugin.plugin_id);
      void loadPlugin(plugin);
    }
    for (const id of Array.from(loadedIdsRef.current)) {
      if (!activeIds.has(id)) unloadPlugin(id);
    }
  }, [installed, user, loadPlugin, unloadPlugin]);

  const activate = useCallback(
    async (pluginId: string) => {
      const { error } = await supabase
        .from("plugins")
        .update({ status: "active", updated_at: new Date().toISOString() })
        .eq("plugin_id", pluginId);
      if (error) throw new Error(error.message);
      await refetchInstalled();
    },
    [refetchInstalled]
  );

  const deactivate = useCallback(
    async (pluginId: string) => {
      const { error } = await supabase
        .from("plugins")
        .update({ status: "inactive", updated_at: new Date().toISOString() })
        .eq("plugin_id", pluginId);
      if (error) throw new Error(error.message);
      unloadPlugin(pluginId);
      await refetchInstalled();
    },
    [refetchInstalled, unloadPlugin]
  );

  const managerFetch = useCallback(async (method: string, path: string, body?: unknown) => {
    const session = await getCurrentSession();
    if (!session?.access_token) throw new Error("Not signed in");
    return fetch(path, {
      method,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  }, []);

  const uninstall = useCallback(
    async (pluginId: string) => {
      unloadPlugin(pluginId);
      const res = await managerFetch("POST", "/api/plugins/uninstall", { pluginId });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Uninstall failed (${res.status})`);
      }
      sdksRef.current.delete(pluginId);
      await refetchInstalled();
      queryClient.invalidateQueries({ queryKey: ["plugin-store"] });
    },
    [managerFetch, refetchInstalled, unloadPlugin]
  );

  const value = useMemo<PluginContextValue>(() => {
    const routes: PluginRouteEntry[] = [];
    const sidebarBySection: Record<BuiltinSection, PluginSidebarItem[]> = {
      communications: [],
      "essential-tools": [],
      advanced: [],
      utilities: [],
    };
    const rootSections: PluginRootSection[] = [];

    for (const { plugin } of loaded) {
      const iconUrl = plugin.manifest.icon
        ? publicUrl(plugin.storage_path, plugin.manifest.icon, plugin.version)
        : null;
      for (const page of plugin.manifest.pages) {
        routes.push({ pluginId: plugin.plugin_id, path: page.path, title: page.title });
        const sidebar = page.sidebar;
        if (!sidebar) continue;
        const item: PluginSidebarItem = {
          pluginId: plugin.plugin_id,
          title: sidebar.label,
          url: page.path,
          iconUrl,
          order: sidebar.order ?? 500,
        };
        if (typeof sidebar.section === "string") {
          sidebarBySection[sidebar.section].push(item);
        } else {
          const rootDef = sidebar.section.root;
          let section = rootSections.find((s) => s.id === rootDef.id);
          if (!section) {
            section = { id: rootDef.id, label: rootDef.label, items: [] };
            rootSections.push(section);
          }
          section.items.push(item);
        }
      }
    }
    for (const key of Object.keys(sidebarBySection) as BuiltinSection[]) {
      sidebarBySection[key].sort((a, b) => a.order - b.order);
    }
    for (const section of rootSections) {
      section.items.sort((a, b) => a.order - b.order);
    }

    return {
      installed,
      isLoading,
      loaded,
      loadErrors,
      routes,
      sidebarBySection,
      rootSections,
      getLoadedPlugin: (pluginId: string) => loaded.find((p) => p.plugin.plugin_id === pluginId),
      activate,
      deactivate,
      uninstall,
      managerFetch,
      refetchInstalled,
    };
  }, [installed, isLoading, loaded, loadErrors, activate, deactivate, uninstall, managerFetch, refetchInstalled]);

  return <PluginContext.Provider value={value}>{children}</PluginContext.Provider>;
}

export function usePlugins(): PluginContextValue {
  const ctx = useContext(PluginContext);
  if (!ctx) throw new Error("usePlugins must be used within PluginProvider");
  return ctx;
}

/** Safe variant for components that may render outside the provider. */
export function usePluginsOptional(): PluginContextValue | null {
  return useContext(PluginContext);
}
